import React, { useState, useEffect, useMemo } from 'react'
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Modal,
  Dropdown,
  Card,
  Row,
  Col,
  Divider,
  Upload,
  Form,
  App,
  Alert,
  Collapse,
  Grid,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  SearchOutlined,
  MoreOutlined,
  UndoOutlined,
  EyeInvisibleOutlined,
  FilterOutlined,
  ClearOutlined,
  CheckOutlined,
  InboxOutlined,
  SyncOutlined,
  ImportOutlined,
} from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import { gamesAPI, categoriesAPI, tagsAPI } from '../services/api'
import { adminAPI, StorageConnector } from '../services/adminAPI'
import { useNavigate } from 'react-router-dom';
import { filterSelectOption, sortByName } from '../utils/searchUtils';
import { SyncBundledGamesModal } from '../components/SyncBundledGamesModal';
import {
  getCreationTypeMeta,
  normalizeCreationType,
  CREATION_TYPE_FILTER_OPTIONS,
} from '../utils/creationTypes';


const { Option } = Select

type GameStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'ARCHIVED';

interface Game {
  id: string
  name: string
  description: string
  iconUrl?: string
  gameUrl: string
  videoUrl?: string
  categoryIds: number[]
  tagIds: number[]
  status: GameStatus
  isFeatured: boolean
  isMobile: boolean
  creationType: string
  createdAt: string
  updatedAt?: string
  publishedAt?: string
  developer?: string
  width?: number
  height?: number
  instructions?: string
}

interface Category {
  id: number
  name: string
  slug: string
}

interface Tag {
  id: number
  name: string
  slug: string
}

const Games: React.FC = () => {
  const { modal } = App.useApp();
  const [games, setGames] = useState<Game[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false)
  const [isSyncBundledModalVisible, setIsSyncBundledModalVisible] = useState(false)
  const [editingGame, setEditingGame] = useState<Game | null>(null)
  const [form] = Form.useForm()
  const [uploadForm] = Form.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [uploading, setUploading] = useState(false)
  const [storageConnectors, setStorageConnectors] = useState<StorageConnector[]>([])
  const { message } = App.useApp()

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: 'all', // 'all', 'DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'ARCHIVED'
    featured: 'all', // 'all', 'featured', 'not-featured'
    developer: 'all', // 'all', or developer name
    creationType: 'all', // 'all', 'upload', 'import', 'gamepix_import', etc.
    isMobile: 'all', // 'all', 'mobile', 'desktop'
    categories: [] as number[],
    tags: [] as number[],
  })

  // Batch selection states
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  // Pagination states
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  })

  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const loadStorageConnectors = async () => {
    try {
      const res = await adminAPI.getStorageConnectors()
      const conns = res.data || []
      setStorageConnectors(conns)
      const activeConn = conns.find((c: any) => c.isActive)
      if (activeConn) {
        uploadForm.setFieldValue('storageProvider', activeConn.id)
      }
    } catch (err) {
      console.warn('Failed to load storage providers:', err)
    }
  }

  // Load games, categories, tags, and storage providers from API
  useEffect(() => {
    loadGames()
    loadCategories()
    loadTags()
    loadStorageConnectors()
  }, [])

  const loadGames = async () => {
    try {
      setLoading(true)
      const response = await gamesAPI.getAllForAdmin()
      setGames(response.data)
    } catch (error) {
      console.error('Error loading games:', error)
      message.error('Failed to load games')
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll(true)
      setCategories(sortByName(response.data || []))
    } catch (error) {
      console.error('Error loading categories:', error)
      message.error('Failed to load categories')
    }
  }

  const loadTags = async () => {
    try {
      const response = await tagsAPI.getAll()
      setTags(sortByName(response.data || []))
    } catch (error) {
      console.error('Error loading tags:', error)
      message.error('Failed to load tags')
    }
  }

  // Filtered games calculation
  const filteredGames = useMemo(() => {
    return games.filter((game) => {
      // Filter by search term
      const matchesSearch = !filters.search ||
        game.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        game.description?.toLowerCase().includes(filters.search.toLowerCase());

      // Filter by status
      const matchesStatus = filters.status === 'all' ||
        game.status === filters.status;

      // Filter by featured
      const matchesFeatured = filters.featured === 'all' ||
        (filters.featured === 'featured' && game.isFeatured) ||
        (filters.featured === 'not-featured' && !game.isFeatured);

      // Filter by developer
      const matchesDeveloper = filters.developer === 'all' ||
        game.developer === filters.developer;

      // Filter by creation type
      const matchesCreationType =
        filters.creationType === 'all' ||
        normalizeCreationType(game.creationType) === filters.creationType;

      // Filter by mobile compatibility
      const matchesMobile = filters.isMobile === 'all' ||
        (filters.isMobile === 'mobile' && game.isMobile) ||
        (filters.isMobile === 'desktop' && !game.isMobile);

      // Filter by categories
      const matchesCategories = filters.categories.length === 0 ||
        filters.categories.some(catId => game.categoryIds?.includes(catId));

      // Filter by tags
      const matchesTags = filters.tags.length === 0 ||
        filters.tags.some(tagId => game.tagIds?.includes(tagId));

      return matchesSearch && matchesStatus && matchesFeatured &&
        matchesDeveloper && matchesCreationType && matchesMobile &&
        matchesCategories && matchesTags;
    });
  }, [games, filters])

  // Filter handlers
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setSelectedRowKeys([]) // Clear selection when filters change
    setPagination({ current: 1, pageSize: pagination.pageSize }) // Reset to first page
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      featured: 'all',
      developer: 'all',
      creationType: 'all',
      isMobile: 'all',
      categories: [],
      tags: [],
    })
    setSelectedRowKeys([])
    setPagination({ current: 1, pageSize: pagination.pageSize })
  }

  // Pagination handler
  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({ current: page, pageSize })
    setSelectedRowKeys([]) // Clear selection when pagination changes
  }

  // Batch operations
  const handleBatchStatusChange = async (status: GameStatus) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select games first')
      return
    }

    try {
      const promises = selectedRowKeys.map(id =>
        gamesAPI.update(id, { status })
      )
      await Promise.all(promises)
      message.success(`${selectedRowKeys.length} games updated to ${status.toLowerCase()} status`)
      loadGames()
      setSelectedRowKeys([])
    } catch (error) {
      console.error('Error updating games:', error)
      message.error('Failed to update games')
    }
  }

  const handleBatchFeaturedChange = async (isFeatured: boolean) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select games first')
      return
    }

    try {
      const promises = selectedRowKeys.map(id =>
        gamesAPI.update(id, { isFeatured })
      )
      await Promise.all(promises)
      message.success(`${selectedRowKeys.length} games ${isFeatured ? 'featured' : 'unfeatured'} successfully`)
      loadGames()
      setSelectedRowKeys([])
    } catch (error) {
      console.error('Error updating games:', error)
      message.error('Failed to update games')
    }
  }

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select games first')
      return
    }

    modal.confirm({
      title: 'Delete Selected Games',
      content: `Are you sure you want to permanently delete ${selectedRowKeys.length} selected games? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const promises = selectedRowKeys.map(id => gamesAPI.delete(id))
          await Promise.all(promises)
          message.success(`${selectedRowKeys.length} games deleted successfully`)
          loadGames()
          setSelectedRowKeys([])
        } catch (error) {
          console.error('Error deleting games:', error)
          message.error('Failed to delete games')
        }
      }
    })
  }

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys as string[])
    },
    onSelectAll: (selected: boolean) => {
      if (selected) {
        const allKeys = filteredGames.map(game => game.id)
        setSelectedRowKeys(allKeys)
      } else {
        setSelectedRowKeys([])
      }
    },
  }

  const navigate = useNavigate();

  const handleAdd = () => {
    setEditingGame(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleUploadClick = () => {
    setIsUploadModalVisible(true);
  }

  const handleUploadSubmit = async (values: any) => {
    if (!fileList.length) {
      message.error('Please select a ZIP file to upload');
      return;
    }

    const file = fileList[0] as any;

    // File validation
    const isZipFile = file.name.toLowerCase().endsWith('.zip');
    if (!isZipFile) {
      message.error('Please upload a valid ZIP file');
      return;
    }

    // File size check (100MB limit)
    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      message.error('File size exceeds the maximum limit of 100MB');
      return;
    }

    const formData = new FormData();

    // Use the originFileObj from Ant Design's Upload component if available
    if (file.originFileObj) {
      formData.append('gameFile', file.originFileObj);
    } else {
      formData.append('gameFile', file);
    }

    // Prepare upload data with all required fields
    // Create URL-friendly slug from name
    const slug = values.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const uploadData = {
      name: values.name,
      slug: slug, // Add the generated slug
      description: values.description || '',
      categoryIds: values.categoryIds || [],
      tagIds: values.tagIds || [],
      status: 'DRAFT', // Default status for new games
      isFeatured: values.isFeatured || false,
      sortOrder: values.sortOrder || 0,
      iconUrl: values.iconUrl || '',
      videoUrl: values.videoUrl || '',
      developer: values.developer || '',
      storageProvider: values.storageProvider || 'active' // Include selected storage provider (active by default)
    };

    // Append the upload data as a JSON string
    formData.append('uploadData', JSON.stringify(uploadData));

    try {
      setUploading(true);
      await gamesAPI.uploadGame(formData);
      message.success('Game uploaded successfully');
      setIsUploadModalVisible(false);
      uploadForm.resetFields();
      setFileList([]);
      // Refresh the games list
      loadGames();
    } catch (error: any) {
      console.error('Upload failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload game. Please try again.';
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }
  }

  const handleEdit = (game: Game) => {
    setEditingGame(game)
    form.setFieldsValue({
      ...game,
      categoryIds: game.categoryIds || [],
      tagIds: game.tagIds || [],
      videoUrl: game.videoUrl || '',
      isFeatured: game.isFeatured || false,
      status: game.status || 'DRAFT',
      iconUrl: game.iconUrl || ''
    })
    setIsModalVisible(true)
  }

  const handleDelete = async (id: string) => {
    try {
      await gamesAPI.delete(id)
      message.success('Game deleted permanently')
      loadGames()
    } catch (error) {
      console.error('Error deleting game:', error)
      message.error('Failed to delete game')
    }
  }

  const handleSoftDelete = async (id: string) => {
    try {
      await gamesAPI.update(id, { status: 'ARCHIVED' });
      message.success('Game archived successfully');
      loadGames();
    } catch (error) {
      console.error('Error archiving game:', error);
      message.error('Failed to archive game');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await gamesAPI.update(id, { status: 'DRAFT' });
      message.success('Game restored to draft status');
      loadGames();
    } catch (error) {
      console.error('Error restoring game:', error);
      message.error('Failed to restore game');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Generate slug from name
      const slug = values.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const gameData = {
        ...values,
        slug: slug,
        categoryIds: values.categoryIds || [],
        tagIds: values.tagIds || [],
        isFeatured: values.isFeatured || false,
        status: values.status || 'DRAFT',
        // Preserve existing iconUrl if not changed in the form
        ...(editingGame?.iconUrl && !values.iconUrl ? { iconUrl: editingGame.iconUrl } : {})
      }

      if (editingGame) {
        await gamesAPI.update(editingGame.id, gameData)
        message.success('Game updated successfully')
      } else {
        await gamesAPI.create(gameData)
        message.success('Game created successfully')
      }

      setIsModalVisible(false)
      form.resetFields()
      loadGames()
    } catch (error) {
      console.error('Error saving game:', error)
      message.error('Failed to save game')
    }
  }

  const handleStatusChange = async (id: string, status: GameStatus) => {
    try {
      await gamesAPI.update(id, { status });
      message.success(`Game status updated to ${status.toLowerCase()} successfully`);
      loadGames();
    } catch (error) {
      console.error('Error updating game status:', error);
      message.error('Failed to update game status');
    }
  };

  const handleFeaturedChange = async (id: string, isFeatured: boolean) => {
    try {
      await gamesAPI.update(id, { isFeatured })
      message.success(`Game ${isFeatured ? 'featured' : 'unfeatured'} successfully`)
      loadGames();
      loadGames()
    } catch (error) {
      console.error('Error updating game featured status:', error)
      message.error('Failed to update game featured status')
    }
  }

  const renderActionCell = (_: any, record: Game) => {
    const actionItems = [
      {
        key: 'edit',
        label: 'Edit',
        icon: <EditOutlined />,
        onClick: () => handleEdit(record),
      },
      record.status === 'ARCHIVED'
        ? {
            key: 'restore',
            label: 'Restore to Draft',
            icon: <UndoOutlined />,
            onClick: () => handleRestore(record.id),
          }
        : {
            key: 'archive',
            label: 'Archive',
            icon: <EyeInvisibleOutlined />,
            onClick: () => handleSoftDelete(record.id),
          },
      {
        key: 'delete',
        label: 'Delete Permanently',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          modal.confirm({
            title: 'Delete Game Permanently',
            content: `Are you sure you want to permanently delete "${record.name || 'this game'}"? This action cannot be undone and will remove all associated data including scores and sessions.`,
            okText: 'Delete Permanently',
            okType: 'danger' as const,
            cancelText: 'Cancel',
            onOk: () => handleDelete(record.id),
          })
        },
      },
    ]

    return (
      <Dropdown
        menu={{
          items: actionItems.map((item) => ({
            key: item.key,
            label: item.label,
            icon: item.icon,
            danger: item.danger,
          })),
          onClick: ({ key }) => {
            const item = actionItems.find((i) => i.key === key)
            if (item?.onClick) item.onClick()
          },
        }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button
          type="text"
          icon={<MoreOutlined style={{ fontSize: 18 }} />}
          onClick={(e) => e.preventDefault()}
          style={{
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </Dropdown>
    )
  }

  const fullColumns = [
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: (_: any, record: Game) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {record.iconUrl ? (
            <img
              src={record.iconUrl}
              alt={record.name}
              style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
            />
          ) : (
            <div style={{
              width: 50,
              height: 50,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 4
            }}>
              <span style={{ fontSize: 20 }}>🎮</span>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search name"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: Game) =>
        record.name?.toLowerCase().includes(value.toLowerCase()) || false,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: false,
      render: (description: string) => (
        <div
          style={{
            maxWidth: 300,
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            lineHeight: '1.5',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxHeight: '4.5em', // 3 lines * 1.5 line-height
          }}
          title={description || 'N/A'} // Show full text on hover
        >
          {description || 'N/A'}
        </div>
      ),
    },
    {
      title: 'Categories',
      dataIndex: 'categoryIds',
      key: 'categoryIds',
      render: (categoryIds: number[]) => (
        <>
          {Array.isArray(categoryIds) && categoryIds.map((categoryId) => {
            const category = categories.find(c => c.id === categoryId);
            return category ? (
              <Tag key={categoryId} color="blue">
                {category.name}
              </Tag>
            ) : null;
          })}
        </>
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tagIds',
      key: 'tagIds',
      render: (tagIds: number[] | null | undefined) => (
        <>
          {Array.isArray(tagIds) && tagIds.map((tagId) => {
            const tag = tags.find(t => t.id === tagId);
            return tag ? (
              <Tag key={tagId} color="geekblue">
                {tag.name}
              </Tag>
            ) : (
              <Tag key={tagId} color="default">
                Tag {tagId}
              </Tag>
            );
          })}
        </>
      ),
    },
    {
      title: 'Featured',
      dataIndex: 'isFeatured',
      key: 'isFeatured',
      width: 100,
      render: (isFeatured: boolean, record: Game) => (
        <Tag
          color={isFeatured ? 'gold' : 'default'}
          style={{ cursor: 'pointer' }}
          onClick={() => handleFeaturedChange(record.id, !isFeatured)}
        >
          {isFeatured ? 'Featured' : 'Regular'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Draft', value: 'DRAFT' },
        { text: 'Pending', value: 'PENDING' },
        { text: 'Approved', value: 'APPROVED' },
        { text: 'Rejected', value: 'REJECTED' },
        { text: 'Archived', value: 'ARCHIVED' },
      ],
      onFilter: (value: any, record: Game) => record.status === value,
      render: (status: GameStatus, record: Game) => {
        const statusColors = {
          DRAFT: 'default',
          PENDING: 'blue',
          APPROVED: 'green',
          REJECTED: 'red',
          ARCHIVED: 'gray',
        };

        return (
          <Tag color={statusColors[status]}>
            {status.charAt(0) + status.slice(1).toLowerCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Developer',
      dataIndex: 'developer',
      key: 'developer',
      width: 120,
      render: (developer: string) => developer || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'creationType',
      key: 'creationType',
      width: 110,
      render: (creationType: string) => {
        const meta = getCreationTypeMeta(creationType);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Device',
      dataIndex: 'isMobile',
      key: 'isMobile',
      width: 90,
      render: (isMobile: boolean) => (
        <Tag color={isMobile ? 'green' : 'blue'}>
          {isMobile ? '📱 Mobile' : '💻 Desktop'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: renderActionCell,
    },
  ]

  const mobileColumns = [
    {
      title: 'Game',
      key: 'name',
      render: (_: any, record: Game) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600 }}>{record.name}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.status}</span>
        </div>
      ),
    },
    {
      title: 'Featured',
      dataIndex: 'isFeatured',
      key: 'isFeatured',
      width: 120,
      render: (isFeatured: boolean, record: Game) => (
        <Tag
          color={isFeatured ? 'gold' : 'default'}
          style={{ cursor: 'pointer' }}
          onClick={() => handleFeaturedChange(record.id, !isFeatured)}
        >
          {isFeatured ? 'Featured' : 'Regular'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: renderActionCell,
    },
  ]

  const columns = isMobile ? mobileColumns : fullColumns

  const filtersApplied = Object.values(filters).some(
    (f) => f !== 'all' && f !== '' && (!Array.isArray(f) || f.length > 0)
  )

  const filtersContent = (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} md={8} lg={6}>
        <Input
          placeholder="Search games..."
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          allowClear
        />
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <Select
          placeholder="Status"
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
          style={{ width: '100%' }}
        >
          <Option value="all">All Status</Option>
          <Option value="DRAFT">Draft</Option>
          <Option value="PENDING">Pending</Option>
          <Option value="APPROVED">Approved</Option>
          <Option value="REJECTED">Rejected</Option>
          <Option value="ARCHIVED">Archived</Option>
        </Select>
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <Select
          placeholder="Featured"
          value={filters.featured}
          onChange={(value) => handleFilterChange('featured', value)}
          style={{ width: '100%' }}
        >
          <Option value="all">All Featured</Option>
          <Option value="featured">Featured</Option>
          <Option value="not-featured">Not Featured</Option>
        </Select>
      </Col>
      <Col xs={12} sm={6} md={4} lg={3}>
        <Select
          placeholder="Mobile"
          value={filters.isMobile}
          onChange={(value) => handleFilterChange('isMobile', value)}
          style={{ width: '100%' }}
        >
          <Option value="all">All Devices</Option>
          <Option value="mobile">Mobile</Option>
          <Option value="desktop">Desktop</Option>
        </Select>
      </Col>
      <Col xs={12} sm={6} md={5} lg={4}>
        <Select
          placeholder="Creation Type"
          value={filters.creationType}
          onChange={(value) => handleFilterChange('creationType', value)}
          style={{ width: '100%' }}
          options={CREATION_TYPE_FILTER_OPTIONS}
        />
      </Col>
      <Col xs={12} sm={6} md={5} lg={4}>
        <Select
          placeholder="Developer"
          value={filters.developer}
          onChange={(value) => handleFilterChange('developer', value)}
          style={{ width: '100%' }}
          showSearch
          allowClear
        >
          <Option value="all">All Developers</Option>
          {Array.from(new Set(games.map(g => g.developer).filter(Boolean))).map((dev) => (
            <Option key={dev} value={dev}>
              {dev}
            </Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <Select
          mode="multiple"
          showSearch
          placeholder="Filter by categories"
          value={filters.categories}
          onChange={(value) => handleFilterChange('categories', value)}
          style={{ width: '100%' }}
          maxTagCount={2}
          optionFilterProp="label"
          filterOption={filterSelectOption}
          options={categories.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <Select
          mode="multiple"
          showSearch
          placeholder="Filter by tags"
          value={filters.tags}
          onChange={(value) => handleFilterChange('tags', value)}
          style={{ width: '100%' }}
          maxTagCount={2}
          optionFilterProp="label"
          filterOption={filterSelectOption}
          options={tags.map((tag) => ({
            value: tag.id,
            label: tag.name,
          }))}
        />
      </Col>
      <Col xs={24} sm={12} md={4} lg={3}>
        <Button
          icon={<ClearOutlined />}
          onClick={clearFilters}
          style={{ width: '100%' }}
        >
          Clear
        </Button>
      </Col>
    </Row>
  )

  return (
    <div>
      <Card>
        {/* Header with title and action buttons */}
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <h2 style={{ margin: 0 }}>Games Management</h2>
          <Space wrap>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'all',
                    icon: <PlusOutlined />,
                    label: 'Add Games Center (All Options)',
                    onClick: () => navigate('/games/add'),
                  },
                  {
                    type: 'divider',
                  },
                  {
                    key: 'manual',
                    icon: <PlusOutlined />,
                    label: 'Add Single Game (Manual)',
                    onClick: () => navigate('/games/add?tab=manual'),
                  },
                  {
                    key: 'json',
                    icon: <UploadOutlined />,
                    label: 'Batch JSON Import',
                    onClick: () => navigate('/games/add?tab=json'),
                  },
                  {
                    key: 'upload',
                    icon: <UploadOutlined />,
                    label: 'Upload HTML5 Game (ZIP to S3)',
                    onClick: () => navigate('/games/add?tab=upload'),
                  },
                  {
                    key: 'bundled',
                    icon: <SyncOutlined />,
                    label: 'Sync Bundled Games (11)',
                    onClick: () => navigate('/games/add?tab=bundled'),
                  },
                  {
                    key: 'external',
                    icon: <ImportOutlined />,
                    label: 'Import Providers (GamePix, GameDistribution, etc.)',
                    onClick: () => navigate('/games/add?tab=external'),
                  },
                ],
              }}
              placement="bottomRight"
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/games/add')}>
                Add Games
              </Button>
            </Dropdown>
          </Space>
        </div>

        {/* Filters Section */}
        {isMobile ? (
          <Collapse
            activeKey={mobileFiltersOpen ? ['filters'] : []}
            onChange={(keys) => setMobileFiltersOpen(keys.length > 0)}
            style={{ marginBottom: 16 }}
          >
            <Collapse.Panel
              key="filters"
              header="Filters"
              extra={
                filtersApplied ? (
                  <Tag color="blue" icon={<FilterOutlined />}>
                    Applied
                  </Tag>
                ) : undefined
              }
            >
              {filtersContent}
            </Collapse.Panel>
          </Collapse>
        ) : (
          <Card size="small" style={{ marginBottom: 16 }}>
            {filtersContent}
          </Card>
        )}

        {/* Batch Operations */}
        {selectedRowKeys.length > 0 && (
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f2f5' }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <span><strong>{selectedRowKeys.length}</strong> games selected</span>
                  <Divider type="vertical" />
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'approve', label: 'Approve', onClick: () => handleBatchStatusChange('APPROVED') },
                        { key: 'reject', label: 'Reject', onClick: () => handleBatchStatusChange('REJECTED') },
                        { key: 'archive', label: 'Archive', onClick: () => handleBatchStatusChange('ARCHIVED') },
                        { key: 'draft', label: 'Move to Draft', onClick: () => handleBatchStatusChange('DRAFT') },
                        { key: 'pending', label: 'Mark as Pending', onClick: () => handleBatchStatusChange('PENDING') },
                      ]
                    }}
                  >
                    <Button size="small" icon={<CheckOutlined />}>
                      Change Status
                    </Button>
                  </Dropdown>
                  <Button
                    size="small"
                    onClick={() => handleBatchFeaturedChange(true)}
                  >
                    Feature
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleBatchFeaturedChange(false)}
                  >
                    Unfeature
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleBatchDelete}
                  >
                    Delete
                  </Button>
                </Space>
              </Col>
              <Col>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedRowKeys([])
                  }}
                >
                  Clear Selection
                </Button>
              </Col>
            </Row>
          </Card>
        )}

        {/* Results Summary */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <span>
              Showing <strong>{filteredGames.length}</strong> of <strong>{games.length}</strong> games
            </span>
            {filtersApplied && (
              <Tag color="blue" icon={<FilterOutlined />}>
                Filters Applied
              </Tag>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredGames}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredGames.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePaginationChange,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={isMobile ? undefined : { x: 1200 }}
          tableLayout={isMobile ? 'fixed' : 'auto'}
        />
      </Card>

      {/* Add/Edit Game Modal */}
      <Modal
        title={editingGame ? 'Edit Game' : 'Add Game'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          form.resetFields()
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Game Name"
            rules={[
              { required: true, message: 'Please enter game name' },
              { min: 2, message: 'Name must be at least 2 characters' },
            ]}
          >
            <Input placeholder="Enter game name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter description' },
              { min: 10, message: 'Description must be at least 10 characters' },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter game description"
            />
          </Form.Item>

          <Form.Item
            name="developer"
            label="Developer"
            rules={[{ required: false }]}
          >
            <Input placeholder="Enter developer name (optional)" />
          </Form.Item>

          <Form.Item
            name="categoryIds"
            label="Categories"
            rules={[{ required: true, message: 'Please select at least one category' }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Select categories"
              style={{ width: '100%' }}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              options={categories.map((category) => ({
                value: category.id,
                label: category.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="tagIds"
            label="Tags"
            rules={[{ required: false }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Select tags"
              style={{ width: '100%' }}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              options={tags.map((tag) => ({
                value: tag.id,
                label: tag.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="iconUrl"
            label="Game Icon URL"
            rules={[
              { required: true, message: 'Please enter icon URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="Enter game icon URL" />
          </Form.Item>

          <Form.Item
            name="gameUrl"
            label="Game URL"
            rules={[
              { required: true, message: 'Please enter game URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="Enter game URL" />
          </Form.Item>

          <Form.Item
            name="videoUrl"
            label="Video URL (Optional)"
            rules={[
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="Enter game trailer/demo video URL" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                initialValue="DRAFT"
              >
                <Select>
                  <Option value="DRAFT">Draft</Option>
                  <Option value="PENDING">Pending</Option>
                  <Option value="APPROVED">Approved</Option>
                  <Option value="REJECTED">Rejected</Option>
                  <Option value="ARCHIVED">Archived</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isFeatured"
                label="Featured"
                initialValue={false}
              >
                <Select>
                  <Option value={true}>Featured</Option>
                  <Option value={false}>Regular</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false)
                form.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingGame ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Game Modal */}
      <Modal
        title="Upload HTML5 Game"
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false)
          uploadForm.resetFields()
          setFileList([])
        }}
        footer={null}
        width={800}
      >
        <Form
          form={uploadForm}
          layout="vertical"
          onFinish={handleUploadSubmit}
        >
          <Form.Item
            name="name"
            label="Game Name"
            rules={[
              { required: true, message: 'Please enter game name' },
              { min: 2, message: 'Name must be at least 2 characters' },
            ]}
          >
            <Input placeholder="Enter game name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter description' },
              { min: 10, message: 'Description must be at least 10 characters' },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter game description"
            />
          </Form.Item>

          <Form.Item
            name="categoryIds"
            label="Categories"
            rules={[{ required: true, message: 'Please select at least one category' }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Select categories"
              style={{ width: '100%' }}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              options={categories.map((category) => ({
                value: category.id,
                label: category.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="tagIds"
            label="Tags"
            rules={[{ required: false }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Select tags"
              style={{ width: '100%' }}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              options={tags.map((tag) => ({
                value: tag.id,
                label: tag.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="gameFile"
            label="HTML5 Game (ZIP file)"
            rules={[{ required: true, message: 'Please upload a ZIP file containing your HTML5 game' }]}
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
          >
            <Upload.Dragger
              name="file"
              accept=".zip"
              fileList={fileList}
              beforeUpload={(file) => {
                // Only allow ZIP files
                const isZip = file.type === 'application/zip' || file.name.endsWith('.zip');
                if (!isZip) {
                  message.error('You can only upload ZIP files!');
                  return Upload.LIST_IGNORE;
                }
                // Limit file size to 100MB
                const isLt100M = file.size / 1024 / 1024 < 100;
                if (!isLt100M) {
                  message.error('File must be smaller than 100MB!');
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={({ fileList: newFileList }) => {
                setFileList(newFileList);
              }}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag ZIP file to this area to upload</p>
              <p className="ant-upload-hint">
                Upload a ZIP file containing your HTML5 game (must include an index.html file)
              </p>
            </Upload.Dragger>
          </Form.Item>

          <Form.Item
            name="storageProvider"
            label="Storage Provider"
            rules={[{ required: true, message: 'Please select a storage provider' }]}
            initialValue="active"
            extra="Configured in Settings > Storage"
          >
            <Select placeholder="Select storage provider">
              {storageConnectors.length > 0 ? (
                storageConnectors.map(c => (
                  <Option key={c.id} value={c.id}>
                    {c.name} ({c.providerType}){c.isActive ? ' - [Active]' : ''}
                  </Option>
                ))
              ) : (
                <>
                  <Option value="active">Active Storage Provider (Default)</Option>
                  <Option value="cloudflare-r2">Cloudflare R2</Option>
                  <Option value="minio">MinIO S3</Option>
                  <Option value="aws-s3">AWS S3</Option>
                </>
              )}
            </Select>
          </Form.Item>

          <Alert
            message="Storage Provider Info"
            description={
              <>
                <p>Game files and extracted HTML5 assets will be stored on your configured S3/R2 storage provider with zero egress fees on Cloudflare R2.</p>
                <p>To add or configure storage credentials and custom domains, visit <strong>Settings &gt; Storage</strong>.</p>
              </>
            }
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Status"
                initialValue="DRAFT"
              >
                <Select>
                  <Option value="DRAFT">Draft</Option>
                  <Option value="PENDING">Pending</Option>
                  <Option value="APPROVED">Approved</Option>
                  <Option value="REJECTED">Rejected</Option>
                  <Option value="ARCHIVED">Archived</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isFeatured"
                label="Featured"
                initialValue={false}
              >
                <Select>
                  <Option value={true}>Featured</Option>
                  <Option value={false}>Regular</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsUploadModalVisible(false)
                uploadForm.resetFields()
                setFileList([])
              }}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={uploading}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Game'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Sync Bundled Games Modal */}
      <SyncBundledGamesModal
        visible={isSyncBundledModalVisible}
        onClose={() => setIsSyncBundledModalVisible(false)}
        onSuccess={loadGames}
      />
    </div>
  )
}

export default Games
